import React, { useMemo, useState } from "react";
import { updateCompetitionStage } from "../../../../api";
import { ID, TournamentParticipant } from "../../../../types";
import { Button } from "../../Button";
import { useTournamentEvent } from "../TournamentEventContext";

interface CompetitionStageNotGeneratedProps {
  stageId: ID;
  participants: TournamentParticipant[];
  selectedParticipantIds: ID[];
  matchDurationMinutes: number;
  onMatchDurationMinutesChange: (duration: number) => void;
  onSelectionChange: (participantIds: ID[]) => void;
}

export const CompetitionStageNotGenerated: React.FC<CompetitionStageNotGeneratedProps> = ({
  stageId,
  participants,
  selectedParticipantIds,
  matchDurationMinutes,
  onMatchDurationMinutesChange,
  onSelectionChange,
}) => {
  const { canManage, enableCalendar, onResultSaved } = useTournamentEvent();
  const [savingSelection, setSavingSelection] = useState(false);
  const selectedParticipantsSet = useMemo(
    () => new Set(selectedParticipantIds),
    [selectedParticipantIds]
  );

  const toggleParticipant = (participantId: ID) => {
    onSelectionChange(
      selectedParticipantIds.includes(participantId)
        ? selectedParticipantIds.filter((id) => id !== participantId)
        : [...selectedParticipantIds, participantId]
    );
  };

  const handleSelectAll = () => {
    onSelectionChange(participants.map((p) => p.id));
  };

  const handleClearSelection = () => {
    onSelectionChange([]);
  };

  const handleSaveSelection = async () => {
    if (!canManage || savingSelection) return;
    if (selectedParticipantIds.length < 2) {
      alert("Seleziona almeno 2 partecipanti.");
      return;
    }
    setSavingSelection(true);
    try {
      await updateCompetitionStage(stageId, { participants: selectedParticipantIds });
      await onResultSaved();
    } catch (error) {
      console.error(error);
      alert("Errore nel salvataggio dei partecipanti dello stage.");
    } finally {
      setSavingSelection(false);
    }
  };

  return (
    <div className="bg-white border border-gray-300 rounded shadow-sm p-5 mt-6">
      <p className="text-sm text-gray-600">
        Stage creato ma non ancora generato.{" "}
        {canManage ? "Seleziona i partecipanti e premi 'Genera stage'." : ""}
      </p>

      {canManage && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-semibold">Partecipanti inclusi</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={handleSelectAll} disabled={!participants.length}>
                Seleziona tutti
              </Button>
              <Button size="sm" variant="ghost" onClick={handleClearSelection} disabled={!selectedParticipantIds.length}>
                Deseleziona
              </Button>
            </div>
          </div>
          {enableCalendar && (
            <div className="mb-4 max-w-xs">
              <label className="block text-sm font-medium text-gray-700">
                Durata match
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    className="w-28 border border-gray-300 px-2 py-1 rounded"
                    value={matchDurationMinutes}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      onMatchDurationMinutesChange(Number.isFinite(next) ? next : 60);
                    }}
                  />
                  <span className="text-sm text-gray-600">minuti</span>
                </div>
              </label>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {participants.map((participant) => (
              <label key={participant.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedParticipantsSet.has(participant.id)}
                  onChange={() => toggleParticipant(participant.id)}
                />
                <span>{participant.name}</span>
              </label>
            ))}
            {participants.length === 0 && (
              <p className="text-sm text-gray-600">Nessun partecipante disponibile.</p>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span>Selezionati: {selectedParticipantIds.length}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSaveSelection}
              disabled={savingSelection || selectedParticipantIds.length < 2}
            >
              {savingSelection ? "Salvataggio..." : "Salva selezione"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
